import React, { useState, useEffect, useMemo } from "react";
import {
	Typography,
	Button,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	TablePagination,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Stack,
	alpha,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Page from "../../components/Page";
import { useNavigate } from "react-router";
import NewCampaignDialog from "./NewCampaignDialog";
import axios from "../../utils/axios";

const statusColorMap = {
	draft: "warning",
	scheduled: "info", 
	published: "success",
};

const statusLabelMap = {
	draft: "Draft",
	scheduled: "Scheduled",
	published: "Published",
};

export default function CreateCampaign() {
	const [campaigns, setCampaigns] = useState([]);
	const [open, setOpen] = useState(false);
	const [showStepper, setShowStepper] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [currentCampaignId, setCurrentCampaignId] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const navigate = useNavigate();

	useEffect(() => {
		fetchCampaigns().then((data) => setCampaigns(data));
	}, []);

	const fetchCampaigns = async () => {
		try {
			const response = await axios.get("campaign");
			console.log(response.data);
			return response.data;
		} catch (error) {
			console.error("Error fetching campaigns:", error);
			return [];
		}
	};

	const handleCreateNew = () => {
		handleDialogOpen();
	};

	const handleEditCampaign = (campaignId) => {
		setIsEditing(true);
		console.log(campaignId);
		handleCampaignSelection({ _id: campaignId });
	};

	const handleDialogOpen = () => {
		setOpen(true);
	};

	const handleDialogClose = () => {
		setOpen(false);
	};

	const handleCampaignSelection = (campaignData) => {
		console.log(campaignData);
		navigate(`/dashboard/campaigns/${campaignData._id}`);
	};

	// Pagination handlers
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Report button handler for Published campaigns
	const handleViewReport = (campaignId) => {
		// Navigate to the campaign report page
		navigate(`/dashboard/campaigns/${campaignId}/report`);
	};

	// Filter and search logic
	const filteredCampaigns = useMemo(() => {
		if (!Array.isArray(campaigns)) return [];

		return campaigns.filter((campaign) => {
			// Safety checks
			if (!campaign || !campaign.name || !campaign.status) return false;

			// Search filter
			const matchesSearch = searchQuery === "" ||
				campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				(campaign.type && campaign.type.toLowerCase().includes(searchQuery.toLowerCase()));

			// Status filter
			const matchesStatus = statusFilter === "all" ||
				campaign.status.toLowerCase() === statusFilter.toLowerCase();

			return matchesSearch && matchesStatus;
		});
	}, [campaigns, searchQuery, statusFilter]);

	// Paginated campaigns
	const paginatedCampaigns = useMemo(() => {
		const startIndex = page * rowsPerPage;
		return filteredCampaigns.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredCampaigns, page, rowsPerPage]);



	// 1. Function to handle deleting a campaign
	const handleDeleteCampaign = async (campaignId) => {
		// Optional: confirm the deletion
		const confirmDelete = window.confirm(
			"Are you sure you want to delete this campaign?"
		);
		if (!confirmDelete) return;

		try {
			await axios.delete(`campaign/${campaignId}`);
			// Update local state to remove the deleted campaign
			setCampaigns((prevCampaigns) =>
				prevCampaigns.filter((c) => c._id !== campaignId)
			);
		} catch (error) {
			console.error("Error deleting campaign:", error);
		}
	};

	return (
		<Page>
			{!showStepper && (
				<>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
							p: 2,
							bgcolor: "background.paper",
							borderRadius: 1,
							boxShadow: 3,
						}}
					>
						<Typography variant="h4" component="h1">
							Campaigns
						</Typography>
						{/* 
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleCreateNew}
            >
              Create
            </Button> 
            */}
					</Box>

					{/* Search and Filters */}
					<Paper
						sx={{
							p: 2,
							mb: 2,
							bgcolor: "background.paper",
							borderRadius: 1,
							boxShadow: 1,
						}}
					>
						<Stack spacing={2}>
							<TextField
								label="Search campaigns"
								variant="outlined"
								fullWidth
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{ maxWidth: 400 }}
							/>
							<FormControl sx={{ minWidth: 120 }}>
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
									label="Status"
								>
									<MenuItem value="all">All Status</MenuItem>
									<MenuItem value="draft">Draft</MenuItem>
									<MenuItem value="scheduled">Scheduled</MenuItem>
									<MenuItem value="published">Published</MenuItem>
								</Select>
							</FormControl>
						</Stack>
					</Paper>
					<TableContainer
						component={Paper}
						sx={{
							mt: 2,
							p: 2,
							borderRadius: 1,
							boxShadow: 3,
							border: "1px solid #e0e0e0",
						}}
					>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
									<TableCell>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Name
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Status
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Type
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Created At
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Scheduled date
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
											Actions
										</Typography>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedCampaigns.map((campaign) => (
									<TableRow
										key={campaign._id}
										hover
										sx={{
											"&:hover": {
												backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
											},
										}}
									>
										<TableCell component="th" scope="row">
											<Typography variant="body2" sx={{ fontWeight: "medium" }}>
												{campaign.name}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={statusLabelMap[campaign.status.toLowerCase()] || campaign.status}
												color={statusColorMap[campaign.status.toLowerCase()] || "secondary"}
												variant="outlined"
												size="small"
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{campaign.type}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{new Date(campaign.createdAt).toLocaleString()}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{campaign.scheduledDate ? new Date(campaign.scheduledDate).toLocaleString() : 'N/A'}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Stack direction="row" spacing={1} justifyContent="flex-end">
												{/* Report Button - Only show for Published campaigns */}
												{campaign.status && campaign.status.toLowerCase() === 'published' && (
													<Tooltip title="View Report">
														<IconButton
															color="info"
															onClick={() => handleViewReport(campaign._id)}
															sx={{
																bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
																"&:hover": {
																	bgcolor: (theme) => alpha(theme.palette.info.main, 0.16),
																},
															}}
														>
															<AssessmentIcon />
														</IconButton>
													</Tooltip>
												)}
												
												{/* Edit/View Button */}
												<Tooltip
													title={campaign.status && campaign.status.toLowerCase() === "draft" ? "Edit" : "View"}
												>
													<IconButton
														color="primary"
														onClick={() => handleEditCampaign(campaign._id)}
														sx={{
															bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
															"&:hover": {
																bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
															},
														}}
													>
														{campaign.status && campaign.status.toLowerCase() === "draft" ? (
															<EditIcon />
														) : (
															<VisibilityIcon />
														)}
													</IconButton>
												</Tooltip>
												
												{/* Delete Button */}
												<Tooltip title="Delete">
													<IconButton
														color="error"
														onClick={() => handleDeleteCampaign(campaign._id)}
														sx={{
															bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
															"&:hover": {
																bgcolor: (theme) => alpha(theme.palette.error.main, 0.16),
															},
														}}
													>
														<DeleteIcon />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								))}
								{paginatedCampaigns.length === 0 && (
									<TableRow>
										<TableCell colSpan={6} align="center">
											<Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
												{filteredCampaigns.length === 0 && campaigns.length > 0
													? "No campaigns match your search criteria"
													: "No campaigns found"}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
						<TablePagination
							rowsPerPageOptions={[5, 10, 25, 50]}
							component="div"
							count={filteredCampaigns.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							sx={{
								"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
									color: "text.secondary",
								},
							}}
						/>
					</TableContainer>
				</>
			)}
			<NewCampaignDialog
				open={open}
				onOpen={handleDialogOpen}
				onClose={handleDialogClose}
				onContinue={handleCampaignSelection}
				initialValues={{
					name: "",
					scheduledDate: new Date(),
					campaignType: "",
					color: "#1890FF",
				}}
			/>
		</Page>
	);
}
